import React, { useEffect, useRef, useState } from "react";
import { Mic2, MessageSquare, TrendingUp, Megaphone, Headphones, GitBranch, CheckCircle2 } from "lucide-react";

const products = [
  {
    id: "voice-agents",
    icon: Mic2,
    title: "AI Voice Agents",
    description:
      "Human-like voice agents that handle inbound and outbound calls with natural conversation, zero latency.",
    useCases: [
      "24/7 inbound call handling",
      "Automated outbound lead follow-up",
      "Appointment confirmation calls",
      "Customer satisfaction surveys",
    ],
    badge: "Voice AI",
  },
  {
    id: "whatsapp-agents",
    icon: MessageSquare,
    title: "AI WhatsApp Agents",
    description:
      "Intelligent WhatsApp bots that qualify leads, provide support, and drive sales — all automatically.",
    useCases: [
      "Lead qualification & scoring",
      "Product catalog browsing",
      "Order tracking & support",
      "Appointment booking via chat",
    ],
    badge: "WhatsApp AI",
  },
  {
    id: "sales-agents",
    icon: TrendingUp,
    title: "AI Sales Agents",
    description:
      "Autonomous sales agents that nurture leads, book appointments, and close deals without human intervention.",
    useCases: [
      "Multi-channel lead nurturing",
      "Automated demo scheduling",
      "CRM data enrichment",
      "Sales pipeline management",
    ],
    badge: "Sales AI",
  },
  {
    id: "marketing-agents",
    icon: Megaphone,
    title: "AI Marketing Agents",
    description:
      "Campaign automation agents that optimize performance, generate content, and personalize outreach at scale.",
    useCases: [
      "Automated campaign optimization",
      "AI content generation",
      "Audience segmentation",
      "Performance reporting",
    ],
    badge: "Marketing AI",
  },
  {
    id: "support-agents",
    icon: Headphones,
    title: "AI Customer Support Agents",
    description:
      "24/7 intelligent support agents that resolve queries, escalate issues, and keep customers satisfied.",
    useCases: [
      "Instant query resolution",
      "Smart ticket routing",
      "FAQ & knowledge base AI",
      "Multi-language support",
    ],
    badge: "Support AI",
  },
  {
    id: "workflow-agents",
    icon: GitBranch,
    title: "Workflow Automation Agents",
    description:
      "Autonomous agents that execute complex business processes, integrate systems, and eliminate repetitive tasks.",
    useCases: [
      "Multi-step process automation",
      "Cross-system data sync",
      "Approval workflow management",
      "Reporting & analytics automation",
    ],
    badge: "Automation AI",
  },
];

const ProductCard = ({ product, index, inView }) => {
  const Icon = product.icon;

  return (
    <div
      data-testid={`product-card-${product.id}`}
      className={`product-card rounded-2xl p-8 flex flex-col transition-all duration-700 ${
        inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${(index % 3) * 100}ms` }}
    >
      {/* Icon */}
      <div className="w-12 h-12 rounded-xl bg-[#00E599]/8 border border-[#00E599]/15 flex items-center justify-center mb-6">
        <Icon size={22} className="text-[#00E599]" />
      </div>

      {/* Badge */}
      <span className="text-xs font-bold text-[#71717A] uppercase tracking-widest mb-2">
        {product.badge}
      </span>

      {/* Title */}
      <h3
        className="text-xl font-bold text-white mb-3"
        style={{ fontFamily: "Outfit, sans-serif" }}
      >
        {product.title}
      </h3>

      {/* Description */}
      <p className="text-sm text-[#A1A1AA] leading-relaxed mb-6 flex-1">
        {product.description}
      </p>

      {/* Use Cases */}
      <div className="space-y-2">
        {product.useCases.map((uc, i) => (
          <div key={i} className="flex items-center gap-2.5">
            <CheckCircle2 size={13} className="text-[#00E599] flex-shrink-0" />
            <span className="text-xs text-[#71717A]">{uc}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Products = () => {
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="products"
      data-testid="products-section"
      ref={sectionRef}
      className="py-24 md:py-32 bg-[#080808]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-16 text-center">
          <span className="text-xs font-bold text-[#00E599] uppercase tracking-widest block mb-4">
            Our AI Product Suite
          </span>
          <h2
            className="text-4xl md:text-5xl font-bold text-white mb-5"
            style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "-0.025em" }}
          >
            Agentic AI Products
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
            Purpose-built AI systems that integrate seamlessly with your existing workflows
            and deliver measurable business outcomes from day one.
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {products.map((product, index) => (
            <ProductCard
              key={product.id}
              product={product}
              index={index}
              inView={inView}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-14 text-center">
          <p className="text-[#71717A] text-sm mb-4">
            Don't see what you need? We build custom AI systems too.
          </p>
          <a
            href="#solutions"
            data-testid="products-custom-cta"
            className="inline-flex items-center gap-2 text-[#00E599] font-semibold text-sm border border-[#00E599]/25 rounded-full px-6 py-3 hover:bg-[#00E599]/8 transition-colors"
          >
            Explore Custom Solutions
          </a>
        </div>
      </div>
    </section>
  );
};

export default Products;
